import { useMemo, useState } from "react";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ClipboardCheck, Printer, FileText } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useCalibrationDueList, useAssetCalibrations, getCalibrationCertificateUrl, type CalibrationDueFilters, useVerifyCalibration } from "@/features/calibration/api";
import { CalibrationKpis, type CalibrationKpiKey } from "@/features/calibration/CalibrationKpis";
import { CalibrationStatusBadge, CalibrationResultBadge } from "@/features/calibration/badges";
import { RecordCalibrationDialog } from "@/features/calibration/RecordCalibrationDialog";
import { printCalibrationLabel } from "@/features/calibration/CalibrationLabel";
import type { CalibrationDueRow, CalibrationKind } from "@/features/calibration/types";
import { useConfigOptions } from "@/features/configuration/api";
import { apiList } from "@/integrations/api/client";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { RouteGuard } from "@/components/auth/RouteGuard";
import { canWrite } from "@/lib/permissions";
import { useBranch } from "@/context/BranchContext";
import { isBranchLocked } from "@/lib/roles";
import { buildAssetQrPayload } from "@/lib/app-config";

export const Route = createFileRoute("/_authenticated/calibration")({
  component: () => (
    <RouteGuard module="calibration"><CalibrationPage /></RouteGuard>
  ),
  validateSearch: (s: Record<string, unknown>) => ({ due: s.due as string | undefined }),
});

const VIEWER_ROLES = new Set(["super_admin", "biomed_head", "biomed_engineer", "facility_manager", "quality", "management", "purchase_stores", "dept_user"]);

function CalibrationPage() {
  const { data: me } = useCurrentUser();
  const roles = me?.roles ?? [];
  const canView = roles.some((r) => VIEWER_ROLES.has(r));
  const canEdit = canWrite("calibration", roles);

  if (!me) return null;
  if (!canView) {
    return <Card className="mx-auto max-w-xl"><CardContent className="space-y-2 p-6">
      <h2 className="text-lg font-semibold">Permission required</h2>
      <p className="text-sm text-muted-foreground">You do not have access to the Calibration module.</p>
    </CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Calibration & QA</h1> <ReadOnlyPill module="calibration" />
        <p className="text-sm text-muted-foreground">Calibration due list, AERB QA tests and history.</p>
      </div>
      <Tabs defaultValue="due">
        <TabsList>
          <TabsTrigger value="due">Calibration Due</TabsTrigger>
          <TabsTrigger value="aerb">AERB QA Tests</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>
        <TabsContent value="due" className="mt-4"><DueListTab kind="Calibration" canEdit={canEdit} /></TabsContent>
        <TabsContent value="aerb" className="mt-4"><DueListTab kind="AERB QA" canEdit={canEdit} /></TabsContent>
        <TabsContent value="history" className="mt-4"><HistoryTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function DueListTab({ kind, canEdit }: { kind: CalibrationKind; canEdit: boolean }) {
  const { data: me } = useCurrentUser();
  const { currentBranchId, setCurrentBranchId, canSwitch } = useBranch();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [departmentId, setDepartmentId] = useState<string>("all");
  const [categoryId, setCategoryId] = useState<string>("all");
  const [search, setSearch] = useState("");
  const { due } = Route.useSearch();
  const [resultFilter, setResultFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>(due === "overdue" ? "Overdue" : due === "soon" ? "Due Soon" : "all");

  const filters: CalibrationDueFilters = {
    overdueOnly,
    departmentId: departmentId === "all" ? null : departmentId,
    categoryId: categoryId === "all" ? null : categoryId,
    branchId: effectiveBranchId,
    requiresAerb: kind === "AERB QA",
    search,
  };
  const { data: rows = [], isLoading } = useCalibrationDueList(filters);
  const lookups = useLookups();
  const { data: resultOptions } = useConfigOptions("calibration", "calibration_result");
  const resultChoices = useMemo(
    () => (resultOptions ?? []).filter((o) => o.is_active).map((o) => o.label),
    [resultOptions],
  );
  const STATUS_CHOICES = ["Overdue", "Due Soon", "Current", "Failed - Restricted", "N/A"];
  const filteredRows = useMemo(() => {
    let r = rows;
    if (resultFilter !== "all") r = r.filter((x) => (x.last_result ?? "") === resultFilter);
    if (statusFilter !== "all") r = r.filter((x) => x.status === statusFilter);
    return r;
  }, [rows, resultFilter, statusFilter]);

  const [recordTarget, setRecordTarget] = useState<CalibrationDueRow | null>(null);

  return (
    <div className="space-y-3">
      <CalibrationKpis
        rows={rows}
        activeKey={(statusFilter === "all" ? null : (statusFilter as CalibrationKpiKey))}
        onSelect={(k) => setStatusFilter(k ?? "all")}
      />
      <Card><CardContent className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input className="w-56" placeholder="Search asset / code…" value={search} onChange={(e) => setSearch(e.target.value)} />
          {canSwitch && (
            <Select value={currentBranchId ?? ""} onValueChange={(v) => setCurrentBranchId(v)}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>
                {lookups.data?.branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={departmentId} onValueChange={setDepartmentId}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {lookups.data?.departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {lookups.data?.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={resultFilter} onValueChange={setResultFilter}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Result" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All results</SelectItem>
              {resultChoices.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_CHOICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant={overdueOnly ? "default" : "outline"} size="sm" onClick={() => setOverdueOnly((v) => !v)}>Overdue only</Button>
        </div>
      </CardContent></Card>


      <Card>
        <CardContent className="p-0">
          {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
          filteredRows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No assets found.</div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Last {kind === "AERB QA" ? "QA" : "Cal."}</TableHead>
                  <TableHead>Next Due</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>Agency</TableHead>
                  <TableHead>Cert No</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((r) => (
                  <TableRow key={r.asset_id}>
                    <TableCell>
                      <Link to="/assets/$assetId" params={{ assetId: r.asset_id }} className="font-medium hover:underline">{r.asset_name}</Link>
                      <div className="font-mono text-xs text-muted-foreground">{r.asset_code}</div>
                    </TableCell>
                    <TableCell className="text-sm">{r.category_name ?? "—"}</TableCell>
                    <TableCell className="text-sm">{r.department_name ?? "—"}</TableCell>
                    <TableCell className="text-sm">{r.last_done_date ?? "—"}</TableCell>
                    <TableCell className="text-sm">{r.next_due_date ?? "—"}</TableCell>
                    <TableCell className="text-sm tabular-nums">{r.days_until_due ?? "—"}</TableCell>
                    <TableCell className="text-sm">{r.agency ?? "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.certificate_no ?? "—"}</TableCell>
                    <TableCell><CalibrationResultBadge value={r.last_result ?? null} /></TableCell>
                    <TableCell><CalibrationStatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canEdit && (
                          <Button size="sm" variant="outline" onClick={() => setRecordTarget(r)}>
                            <ClipboardCheck className="mr-1 h-3 w-3" />Record
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => printCalibrationLabel({
                          asset_code: r.asset_code, asset_name: r.asset_name,
                          done_date: r.last_done_date, next_due_date: r.next_due_date,
                          certificate_no: r.certificate_no, agency: r.agency,
                          qrPayload: buildAssetQrPayload(r.asset_code),
                        })}>
                          <Printer className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {recordTarget && (
        <RecordCalibrationDialog
          open={!!recordTarget}
          onOpenChange={(v) => { if (!v) setRecordTarget(null); }}
          assetId={recordTarget.asset_id}
          assetName={recordTarget.asset_name}
          assetCode={recordTarget.asset_code}
          assetBranchId={recordTarget.branch_id}
          kind={kind}
        />
      )}
    </div>
  );
}

function HistoryTab() {
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["calibration_history_all", effectiveBranchId],
    queryFn: async () => {
      try {
        const params: any = { limit: 500 };
        if (effectiveBranchId) params.branch = effectiveBranchId;
        const res = await apiList("/api/v1/calibration/", params);
        return res.results || [];
      } catch (e) {
        return [];
      }
    },
  });


  const verify = useVerifyCalibration();

  async function openCert(p: string) {
    if (p.startsWith("http")) {
      window.open(p, "_blank");
      return;
    }
    const w = window.open("about:blank", "_blank");
    try {
      const u = await getCalibrationCertificateUrl(p);
      if (w) w.location.href = u;
      else window.location.href = u;
    } catch (e) {
      if (w) w.close();
      toast.error((e as Error).message);
    }
  }

  const canVerify = me?.roles.includes("quality") || me?.roles.includes("super_admin");

  async function handleVerify(id: string, status: "Verified" | "Rejected") {
    try {
      await verify.mutateAsync({ id, status });
      toast.success(`Calibration marked as ${status}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <Card><CardContent className="p-0">
      {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
      rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No calibration records yet.</div> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Asset</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Done Date</TableHead>
              <TableHead>Next Due</TableHead>
              <TableHead>Agency</TableHead>
              <TableHead>Cert No</TableHead>
              <TableHead>Result</TableHead>
              <TableHead>Verification</TableHead>
              <TableHead className="text-right">Cert</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(rows as import("@/features/calibration/types").CalibrationRow[]).map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  {r.asset && (
                    <Link to="/assets/$assetId" params={{ assetId: r.asset }} className="font-medium hover:underline">{r.asset_name}</Link>
                  )}
                  <div className="font-mono text-xs text-muted-foreground">{r.asset_code}</div>
                </TableCell>
                <TableCell className="text-sm">{r.kind}</TableCell>
                <TableCell className="text-sm">{r.calibration_date ?? "—"}</TableCell>
                <TableCell className="text-sm">{r.next_due_date ?? "—"}</TableCell>
                <TableCell className="text-sm">{r.calibration_type === "Vendor" ? r.vendor_name || "Vendor" : "Internal"}</TableCell>
                <TableCell className="font-mono text-xs">{r.certificate_number ?? "—"}</TableCell>
                <TableCell><CalibrationResultBadge value={r.status as any} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                      r.verification_status === "Verified" ? "bg-green-100 text-green-800" :
                      r.verification_status === "Rejected" ? "bg-red-100 text-red-800" :
                      "bg-yellow-100 text-yellow-800"
                    }`}>
                      {r.verification_status}
                    </span>
                    {r.verification_status === "Pending" && canVerify && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-green-600" onClick={() => handleVerify(r.id, "Verified")}>Verify</Button>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-red-600" onClick={() => handleVerify(r.id, "Rejected")}>Reject</Button>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {r.certificate_file ? (
                    <Button size="sm" variant="ghost" onClick={() => openCert(r.certificate_file!)}>
                      <FileText className="h-4 w-4" />
                    </Button>
                  ) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent></Card>
  );
}

function useLookups() {
  return useQuery({
    queryKey: ["calibration_lookups"],
    queryFn: async () => {
      const [b, d, c] = await Promise.all([
        apiList<{ id: string; name: string }>("/api/v1/locations/branches/", { limit: 1000 }),
        apiList<{ id: string; name: string }>("/api/v1/locations/departments/", { limit: 1000 }),
        apiList<{ id: string; name: string }>("/api/v1/configuration/asset-categories/", { limit: 1000 }),
      ]);
      return {
        branches: b.results ?? [],
        departments: d.results ?? [],
        categories: c.results ?? [],
      };
    },
    staleTime: 60_000,
  });
}

// Suppress unused-import warning when historic helper is needed by tabs in future
export { useAssetCalibrations };
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _useMemoUsed = useMemo;
